import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {useEffect, useMemo, useState} from 'react';
import {DataGrid, GridColDef} from '@mui/x-data-grid';
import AddDeliveryModal from 'src/components/Modals/AddDelivery';
import SearchApartments from 'src/components/SearchApartment';
// import {Apartment, Deliveries, Delivery} from 'src/apis/autoGenerate';
// import {deliveryServiceDelete, deliveryServiceFind} from 'src/apis';
import {Apartment} from 'src/apis/autoGenerate/types/apartment';
import {Deliveries} from 'src/apis/autoGenerate/types/delivery';
import {Delivery} from 'src/apis/autoGenerate/types/delivery';
import {deliveryServiceDelete, deliveryServiceFind} from 'src/apis/autoGenerate/services';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import message from 'src/components/Message';
import SearchInput from 'src/components/SearchInput';
import {formatDate} from 'src/utils/common';

export default function WelcomePage() {
  const [AddDeliveryModalOpen, setAddDeliveryModalOpen] = useState(false);
  const [searchApartment, setSearchApartment] = useState<Apartment | null>();
  const [searchInfo, setSearchInfo] = useState<string>('');
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState<Delivery>();
  const [deliveryActionIdAnchorEl, setDeliveryActionIdAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const deliveryActionOpen = Boolean(deliveryActionIdAnchorEl);
  const deliveryActionId = deliveryActionOpen ? 'tenant-action-popover' : undefined;

  useEffect(() => {
    getList();
  }, []);

  useEffect(() => {
    if (!AddDeliveryModalOpen) {
      setSelectedDelivery(undefined);
    }
  }, [AddDeliveryModalOpen]);

  const handleActionClose = () => {
    setDeliveryActionIdAnchorEl(null);
  };

  const handleSearchByApartment = (val: Apartment | null) => {
    setSearchApartment(val ?? undefined);
    getList(searchInfo, val?.id);
  };

  const handleSearchSchedule = (info = '') => {
    // setSearchInfo(info);
    // getList(info, searchApartment?.id);
  };

  const getList = async (name?: string, apartmentId?: string) => {
    setLoading(true);
    deliveryServiceFind(undefined, undefined, [apartmentId as string], name)
      .then(res => {
        const deliveries = (res as unknown as Deliveries).deliveries;
        if (deliveries?.length) {
          setDeliveries(deliveries);
        } else {
          setDeliveries([]);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleActionClick = (event: React.MouseEvent<HTMLButtonElement>, delivery: Delivery) => {
    setSelectedDelivery(delivery);
    setDeliveryActionIdAnchorEl(event.currentTarget);
  };

  const handleDeliveryEdit = () => {
    setDeliveryActionIdAnchorEl(null);
    setAddDeliveryModalOpen(true);
  };

  const handleRemoveDelivery = () => {
    setLoading(true);
    deliveryServiceDelete(selectedDelivery?.id as string).then(() => {
      message.success('Remove Success');
      getList(searchInfo, searchApartment?.id);
    });
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        width: 130,
        flex: 1,
      },
      {
        field: 'apartment',
        headerName: 'Schedule',
        width: 130,
        flex: 1,
        renderCell: (val: any) => {
          return <div>{val.value.name}</div>;
        },
      },
      {
        field: 'createdAt',
        headerName: 'Device Name',
        width: 130,
        flex: 2,
        renderCell: (val: any) => {
          return (
            <div>
              {formatDate(val.row.validDateFrom)} - {formatDate(val.row.validDateTo)}
            </div>
          );
        },
      },
      {
        field: 'id',
        headerName: '',
        width: 60,
        renderCell: val => {
          return (
            <IconButton
              onClick={e => {
                handleActionClick(e, val.row);
              }}
            >
              <MoreHorizIcon />
            </IconButton>
          );
        },
      },
    ],
    []
  );

  return (
    <Box>
      <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
        <Stack sx={{display: 'flex'}} spacing={2} direction="row" alignItems="center">
          <SearchInput placeholder="Search Schedule" onSearch={handleSearchSchedule} />
          <SearchApartments placeholder="Select an apartment" onChange={handleSearchByApartment} />
        </Stack>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            onClick={() => {
              setSelectedDelivery(undefined);
              setAddDeliveryModalOpen(true);
            }}
            startIcon={<AddIcon />}
            sx={{borderRadius: 16, height: 40}}
          >
            New
          </Button>
        </Stack>
      </Box>
      <Box mt={2}>
        <DataGrid
          loading={loading}
          disableColumnFilter
          disableColumnMenu
          disableColumnSelector
          rows={deliveries}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: {page: 0, pageSize: 10},
            },
          }}
          slots={{
            noRowsOverlay: () => (
              <Box textAlign="center" my={2}>
                No Deliveries
              </Box>
            ),
          }}
          pageSizeOptions={[5, 10]}
          disableRowSelectionOnClick
          autoHeight
        />
      </Box>
      {AddDeliveryModalOpen && (
        <AddDeliveryModal
          open={AddDeliveryModalOpen}
          initData={selectedDelivery}
          handleCancel={() => {
            setAddDeliveryModalOpen(false);
          }}
          handleConfirm={() => {
            setAddDeliveryModalOpen(false);
            getList();
          }}
        />
      )}
      <Popover
        id={deliveryActionId}
        open={deliveryActionOpen}
        anchorEl={deliveryActionIdAnchorEl}
        onClose={handleActionClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={handleDeliveryEdit}>
              <ListItemText primary="Edit" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                setDeliveryActionIdAnchorEl(null);
                handleRemoveDelivery();
              }}
            >
              <ListItemText primary="Remove" />
            </ListItemButton>
          </ListItem>
        </List>
      </Popover>
    </Box>
  );
}
